import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Country } from "country-state-city";
import { handleFetchBusinessById, handleFetchKybCountries } from "api/onboarding";
import { checkAutoLogin } from "store/authServices";
import { toastBackendError } from "utilities/toastBackendError";
import useKybCountries from "hooks/useKybCountries";
import { emptyFileState } from "./constants";
import {
  getEmptyRequirementValue,
  getPrefillDataForType,
  getRequirementTargetId,
  hasValue,
  isApproved,
  isRejectedDirectorRequirement,
  isRejectedUboRequirement,
  normalizeKey,
  normalizeRejectedResponse,
  normalizeRequirement,
  validateFile,
} from "./normalizationUtils";
import { buildPayload } from "./payloadBuilder";

export const useUpdateInformation = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { business } = useSelector((state) => state);

  const businessCountry = business?.businessCountry;
  const country = businessCountry?.name || business?.country;

  const { data: kybCountriesData, isLoading: kybCountriesLoading } = useKybCountries();
  const countryOptions = kybCountriesData?.countryOptions || [];

  const [phoneCountryCode, setPhoneCountryCode] = useState("");
  const [phoneCountryOptions, setPhoneCountryOptions] = useState([]);
  const [phoneCountryTouched, setPhoneCountryTouched] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  const [countriesLoading, setCountriesLoading] = useState(false);
  const [moreData, setMoreData] = useState({ director: {}, ubo: {} });

  const [businessName, setBusinessName] = useState("");
  const [requestStatus, setRequestStatus] = useState("");
  const [requirements, setRequirements] = useState([]);
  const [values, setValues] = useState({});
  const [files, setFiles] = useState({});
  const [rawRejectedData, setRawRejectedData] = useState([]);
  const [businessData, setBusinessData] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const getPhoneDialCode = useCallback(
    (code) => phoneCountryOptions.find((option) => option.code === code)?.dialCode || "",
    [phoneCountryOptions]
  );

  const handleUpdateMoreData = (key, value) => {
    setMoreData((prev) => ({ ...prev, [key]: value }));
  };

  const normalizeCountriesResponse = (responseBody) =>
    (Array.isArray(responseBody?.data) && responseBody.data) ||
    (Array.isArray(responseBody?.data?.countries) && responseBody.data.countries) ||
    (Array.isArray(responseBody?.countries) && responseBody.countries) ||
    (Array.isArray(responseBody) && responseBody) ||
    [];

  useEffect(() => {
    let ignore = false;

    const fetchCountries = async () => {
      setCountriesLoading(true);
      try {
        const responseBody = await handleFetchKybCountries();
        const countries = normalizeCountriesResponse(responseBody);
        const options = countries
          .map((item) => {
            const isoCode = String(item.code || "").toUpperCase();
            const countryMeta = Country.getCountryByCode(isoCode);
            const phonecode = item.phoneCode || item.phonecode || countryMeta?.phonecode;

            if (!isoCode || !phonecode) return null;

            return {
              name: item.name,
              code: isoCode,
              flag: item.flag || countryMeta?.flag || "",
              continent: item.continent || countryMeta?.continent || "",
              dialCode: phonecode.startsWith("+") ? phonecode : `+${phonecode}`,
            };
          })
          .filter(Boolean);

        if (!ignore) {
          setPhoneCountryOptions(options);
        }
      } catch (err) {
        if (!ignore) {
          setError(
            err?.message || "Unable to load phone countries. Please refresh and try again."
          );
        }
      } finally {
        if (!ignore) setCountriesLoading(false);
      }
    };

    fetchCountries();

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    if (phoneCountryTouched || !phoneCountryOptions.length) return;
    const countryCode = businessCountry?.code || business?.countryCode;
    const matchingCountry = phoneCountryOptions.find(
      (option) =>
        option.code === countryCode ||
        normalizeKey(option.name) === normalizeKey(country)
    );
    if (matchingCountry) {
      setPhoneCountryCode(matchingCountry.code);
    } else if (phoneCountryOptions.length > 0 && !phoneCountryCode) {
      setPhoneCountryCode(phoneCountryOptions[0].code);
    }
  }, [phoneCountryOptions, phoneCountryTouched, businessCountry, business, country, phoneCountryCode]);

  const routeBusinessId = searchParams.get("businessId") || business?._id || business?.businessId;
  const currentBusinessId = business?._id || business?.businessId;

  useEffect(() => {
    let ignore = false;

    const fetchRejectedData = async () => {
      try {
        setConfigLoading(true);
        setError("");

        if (routeBusinessId && currentBusinessId && routeBusinessId !== currentBusinessId) {
          await axios.put(`/v1/business/current/${routeBusinessId}`);
          axios.defaults.headers.common["x-business-id"] = routeBusinessId;
          await checkAutoLogin(dispatch);
        }

        const response = await axios.get("/v1/kyb/rejected-data", {
          headers: routeBusinessId ? { "x-business-id": routeBusinessId } : undefined,
        });

        if (ignore) return;

        const normalized = normalizeRejectedResponse(response);
        const nextRequirements = normalized.rejectedData
          .filter((requirement) => requirement && !isApproved(requirement))
          .map(normalizeRequirement);

        const nextValues = {};
        const nextFiles = {};
        nextRequirements.forEach((requirement) => {
          const val = getEmptyRequirementValue(requirement);
          nextValues[requirement.id] = val;

          if (requirement.type === "document" || requirement.type === "upload") {
            nextFiles[requirement.id] = emptyFileState;
          } else if (
            requirement.type === "source_of_fund" ||
            requirement.type === "source_of_funds"
          ) {
            nextFiles[`${requirement.id}-source`] = emptyFileState;
          }
        });

        setBusinessName(normalized.businessName);
        setRequestStatus(normalized.status);
        setRequirements(nextRequirements);
        setRawRejectedData(normalized.rejectedData);
        setValues(nextValues);
        setFiles(nextFiles);

        const targetBizId = routeBusinessId || currentBusinessId;
        if (targetBizId) {
          try {
            const bizRes = await handleFetchBusinessById(targetBizId);
            if (!ignore) {
              setBusinessData(bizRes?.business || bizRes);
            }
          } catch (e) {
            console.error("Failed to fetch business details for prefill:", e);
          }
        }
      } catch (err) {
        if (!ignore) {
          setError(toastBackendError(err, "Unable to load requested information."));
        }
      } finally {
        if (!ignore) setConfigLoading(false);
      }
    };

    fetchRejectedData();

    return () => {
      ignore = true;
    };
  }, [routeBusinessId, currentBusinessId, dispatch]);

  const canSubmit =
    requirements.length > 0 &&
    requirements.some((requirement) => hasValue(values, files, requirement, moreData)) &&
    !loading;

  // Deduplicate director/UBO requirements by targetKey (only the first requirement per
  // target triggers an AddDirectors / ControlPerson form) and pre-attach the prefill
  // data for that target so children receive a stable, ready value on first mount.
  // Doing this at the data layer avoids the mutable-Set-passed-as-prop anti-pattern
  // which caused the director form to silently disappear after the modular refactor.
  const renderableRequirements = useMemo(() => {
    const effectiveBusiness = businessData || business;
    const seenDirectorTargets = new Set();
    const seenUboTargets = new Set();

    return requirements.reduce((acc, requirement) => {
      const requirementType = normalizeKey(requirement.type || "");

      if (requirementType === "director" && isRejectedDirectorRequirement(requirement)) {
        const targetKey = getRequirementTargetId(requirement, true) || "default-director";
        if (seenDirectorTargets.has(targetKey)) return acc; // skip duplicate
        seenDirectorTargets.add(targetKey);
        acc.push({
          ...requirement,
          _formType: "director",
          _prefillData: getPrefillDataForType(
            "director",
            requirement,
            rawRejectedData,
            effectiveBusiness
          ),
        });
      } else if (requirementType === "ubo" && isRejectedUboRequirement(requirement)) {
        const targetKey = getRequirementTargetId(requirement, false) || "default-ubo";
        if (seenUboTargets.has(targetKey)) return acc; // skip duplicate
        seenUboTargets.add(targetKey);
        acc.push({
          ...requirement,
          _formType: "ubo",
          _prefillData: getPrefillDataForType(
            "ubo",
            requirement,
            rawRejectedData,
            effectiveBusiness
          ),
        });
      } else {
        acc.push(requirement);
      }
      return acc;
    }, []);
  }, [requirements, rawRejectedData, businessData, business]);

  const updateValue = (id, nextValue) => {
    setValues((prev) => ({ ...prev, [id]: nextValue }));
    setError("");
  };

  const updateAddress = (id, field, nextValue) => {
    setValues((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] || {}),
        [field]: nextValue,
      },
    }));
    setError("");
  };

  const handleFileChange = (id, event) => {
    const file = event.target.files?.[0];
    const fileError = validateFile(file);

    if (fileError) {
      setError(fileError);
      return;
    }

    setFiles((prev) => ({
      ...prev,
      [id]: { file, loading: false, uploaded: true, progress: 100 },
    }));
    setError("");
  };

  const removeFile = (id) => {
    setFiles((prev) => ({ ...prev, [id]: emptyFileState }));
  };

  const handleSubmit = async () => {
    if (!canSubmit || loading) return;

    try {
      setLoading(true);
      setError("");
      setSuccess("");

      setFiles((prev) => {
        const next = { ...prev };
        requirements.forEach((requirement) => {
          const ids =
            requirement.type === "web_presence"
              ? [`${requirement.id}-web`]
              : requirement.type === "source_of_fund" ||
                requirement.type === "source_of_funds"
                ? [`${requirement.id}-source`]
                : ["document", "upload"].includes(requirement.type)
                  ? [requirement.id]
                  : [];

          ids.forEach((id) => {
            if (next[id]?.file) {
              next[id] = { ...next[id], loading: true, progress: 20 };
            }
          });
        });
        return next;
      });

      const payload = await buildPayload({
        requirements,
        values,
        files,
        moreData,
        phoneCountryCode,
        phoneCountryOptions,
        getPhoneDialCode,
        businessCountry,
        kybCountriesData,
      });

      await axios.patch("/v1/kyb/resubmit", payload, {
        headers: routeBusinessId ? { "x-business-id": routeBusinessId } : undefined,
      });

      setSuccess("Information submitted successfully.");
      navigate("/onboarding/successfully-updated", { replace: true });
    } catch (err) {
      setError(toastBackendError(err, "Unable to submit this information. Please try again."));
    } finally {
      setLoading(false);
      setFiles((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((id) => {
          next[id] = {
            ...next[id],
            loading: false,
            uploaded: Boolean(next[id]?.file),
            progress: next[id]?.file ? 100 : 0,
          };
        });
        return next;
      });
    }
  };

  return {
    requirements,
    renderableRequirements,
    values,
    files,
    businessName,
    requestStatus,
    rawRejectedData,
    businessData,
    business,
    configLoading,
    loading,
    error,
    success,
    canSubmit,
    country,
    countryOptions,
    kybCountriesLoading,
    phoneCountryCode,
    phoneCountryOptions,
    countriesLoading,
    phoneError,
    getPhoneDialCode,
    setPhoneCountryTouched,
    setPhoneCountryCode,
    setPhoneError,
    updateValue,
    updateAddress,
    handleFileChange,
    removeFile,
    handleUpdateMoreData,
    handleSubmit,
  };
};
